import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button, Chip, Typography } from "@mui/material";
import FilePicker from "components/FilePicker";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import ImagePreviewBox from "components/ImagePreviewBox";
import SearchableInput from "components/SearchableInput";
import React from "react";
import useStyles from "styles/banner/bannerEditor";
import { DEFAULT_IMAGE_DIMENSIONS, ImageType } from "utils/imageConstants";

export const AWARD_TYPES = [
  { id: "COINS", label: "Coins" },
  { id: "PACK", label: "Pack" },
  { id: "BOOSTER", label: "Booster" },
  { id: "XP", label: "XP" },
];

const SLOT_IMAGE_TYPE = ImageType.CHALLENGE_REWARD_IMAGE;

// The only reward type with more than one sensible default image. The three artworks are always
// offered, whether or not each has been given an image under Challenge Reward Images yet. The
// labels carry no coin ranges on purpose: the amount only picks the first suggestion, and any
// amount may deliberately be given any of the three.
const COIN_IMAGE_TYPES = [
  { id: "PBF_COINS_SMALL", label: "Small" },
  { id: "PBF_COINS_MEDIUM", label: "Medium" },
  { id: "PBF_COINS_LARGE", label: "Large" },
];

const XP_IMAGE_TYPE = "XP";
const PACK_IMAGE_TYPE = "PACK";

/** Which of the three coin artworks an amount falls into. */
export const coinImageTypeFor = (rewardHC?: number | null): string => {
  const amount = Number(rewardHC) || 0;

  if (amount <= 2000) return COIN_IMAGE_TYPES[0].id;

  return amount < 10000 ? COIN_IMAGE_TYPES[1].id : COIN_IMAGE_TYPES[2].id;
};

/**
 * The image type a reward actually resolves to. Coins are the only case worth asking the admin
 * about, and even there the amount picks a sensible one first; XP has exactly one image. A pack
 * reward always carries the pack image type too: the pack's own artwork wins when it has any, and
 * uploading one here is optional, so without this a pack with no image of its own would leave the
 * reward with no picture at all. Boosters are the one type with no default to fall back to.
 *
 * This is what the editor stores, so what is saved always matches what was shown.
 */
export const resolveRewardImageType = (reward: any): string | null => {
  switch (reward?.awardType) {
    case "COINS":
      return COIN_IMAGE_TYPES.some((type) => type.id === reward.rewardImageType)
        ? reward.rewardImageType
        : coinImageTypeFor(reward.rewardHC);
    case "XP":
      return XP_IMAGE_TYPE;
    case "PACK":
      return PACK_IMAGE_TYPE;
    default:
      return null;
  }
};

export type RewardOption = {
  id: string;
  label: string;
  imageUrl?: string | null;
};

export type RewardSharedProps = {
  packOptions: RewardOption[];
  boosterOptions: RewardOption[];
  rewardImageOptions: RewardOption[];
  onPackSearch: (term: string) => void;
  onBoosterSearch?: (term: string) => void;
};

type Props = RewardSharedProps & {
  value: any;
  fieldPrefix: string;
  onChange: (patch: Record<string, any>) => void;
};

/** One-line description of what a reward awards, used in the collapsed slot summary. */
export const describeReward = (reward: any): string => {
  switch (reward?.awardType) {
    case "COINS":
      return reward.rewardHC ? `${reward.rewardHC} coins` : "Coins — not set";
    case "XP":
      return reward.xpAmount ? `${reward.xpAmount} XP` : "XP — not set";
    case "PACK":
      return reward.pack ? `Pack ×${reward.count || 1}` : "Pack — not selected";
    case "BOOSTER":
      return reward.booster ? "Booster" : "Booster — not selected";
    default:
      return "Not configured";
  }
};

export const isRewardComplete = (reward: any): boolean => {
  switch (reward?.awardType) {
    case "COINS":
      return reward.rewardHC > 0;
    case "XP":
      return reward.xpAmount > 0;
    case "PACK":
      return !!reward.pack;
    case "BOOSTER":
      return !!reward.booster;
    default:
      return false;
  }
};

/**
 * The reward-type-driven field group. Base rewards and per-operator overrides share it, so both
 * behave identically and a new reward type only has to be handled once.
 */
const RewardFields = ({
  value,
  fieldPrefix,
  onChange,
  packOptions,
  boosterOptions,
  rewardImageOptions,
  onPackSearch,
  onBoosterSearch,
}: Props) => {
  const styles = useStyles();

  const selectedPack = value.pack
    ? (packOptions.find((option) => option.id === value.pack) ?? {
        id: value.pack,
        label: value.pack,
      })
    : null;

  const selectedBooster = value.booster
    ? (boosterOptions.find((option) => option.id === value.booster) ?? {
        id: value.booster,
        label: value.booster,
      })
    : null;

  const effectiveRewardImageType = resolveRewardImageType(value);

  const coinImageOptions: RewardOption[] = COIN_IMAGE_TYPES.map((type) => ({
    ...type,
    imageUrl:
      rewardImageOptions.find((option) => option.id === type.id)?.imageUrl ??
      null,
  }));

  const selectedRewardImage = (
    value.awardType === "COINS" ? coinImageOptions : rewardImageOptions
  ).find((option) => option.id && option.id === effectiveRewardImageType);

  // The preview mirrors exactly what the backend will resolve at claim time: the upload wins, then
  // the pack or booster artwork, then the chosen reward-type image. A freshly picked file previews
  // instantly from its object URL, so nothing here waits on an upload round trip.
  const packImageUrl =
    value.awardType === "PACK"
      ? (selectedPack as RewardOption)?.imageUrl
      : null;
  const boosterImageUrl =
    value.awardType === "BOOSTER"
      ? (selectedBooster as RewardOption)?.imageUrl
      : null;

  const resolved: { url: string; badge: string; caption: string } | null =
    value.__previewUrl
      ? {
          url: value.__previewUrl,
          badge: "New",
          caption: "New upload — stored when you save",
        }
      : value.imageUrl
        ? {
            url: value.imageUrl,
            badge: "Uploaded",
            caption: "Uploaded image — used ahead of every fallback",
          }
        : packImageUrl
          ? {
              url: packImageUrl,
              badge: "Pack",
              caption: "The selected pack's own image",
            }
          : boosterImageUrl
            ? {
                url: boosterImageUrl,
                badge: "Booster",
                caption: "The selected booster's own image",
              }
            : selectedRewardImage?.imageUrl
              ? {
                  url: selectedRewardImage.imageUrl,
                  badge: "Default",
                  caption: `Default image — ${selectedRewardImage.label}`,
                }
              : null;

  const emptyCaption = selectedRewardImage
    ? `No image set for "${selectedRewardImage.label}" — upload one here, or configure it under Challenges → Challenge Reward Images`
    : value.awardType === "PACK"
      ? "Select a pack with its own image, upload one here, or set the PACK default under Challenges → Challenge Reward Images"
      : value.awardType === "BOOSTER"
        ? "Select a booster to use its own image, or upload one here"
        : "Upload an image here";

  const priorityCaption =
    value.awardType === "COINS"
      ? "Your upload wins; otherwise the selected coin image is used."
      : value.awardType === "XP"
        ? "Your upload wins; otherwise the XP image is used."
        : value.awardType === "PACK"
          ? "Your upload wins, then the selected pack's own image, then the default pack image."
          : "Your upload wins; otherwise the selected booster's own image is used.";

  const releasePreview = () => {
    if (value.__previewUrl) URL.revokeObjectURL(value.__previewUrl);
  };

  const clearImage = () => {
    releasePreview();

    onChange({
      imagePath: null,
      imageUrl: null,
      __file: null,
      __dims: null,
      __previewUrl: null,
    });
  };

  return (
    // flex-start, not the default stretch: the preview column keeps its own height instead of being
    // pulled down to match the taller side, which is what left the empty strip at the bottom.
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: 3,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 320 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Reward Type"
              name={`${fieldPrefix}_awardType`}
              value={value.awardType ?? ""}
              options={AWARD_TYPES}
              onChange={(e: any) => {
                const awardType = e.target.value;

                onChange({
                  awardType,
                  rewardHC: null,
                  xpAmount: null,
                  pack: null,
                  booster: null,
                  count: null,
                  rewardImageType: resolveRewardImageType({ awardType }),
                });
              }}
            />
          </Box>

          {value.awardType === "COINS" && (
            <Box className={styles.smallField}>
              <SimpleTextInput
                label="Coins"
                name={`${fieldPrefix}_rewardHC`}
                type="number"
                value={value.rewardHC ?? ""}
                onChange={(e: any) => {
                  const rewardHC =
                    e.target.value === "" ? null : Number(e.target.value);

                  // The amount picks the artwork, but only while the admin has not overridden it:
                  // a deliberate choice must survive a later tweak to the amount.
                  const wasAutoPicked =
                    !value.rewardImageType ||
                    value.rewardImageType === coinImageTypeFor(value.rewardHC);

                  onChange({
                    rewardHC,
                    ...(wasAutoPicked && {
                      rewardImageType: coinImageTypeFor(rewardHC),
                    }),
                  });
                }}
              />
            </Box>
          )}

          {value.awardType === "XP" && (
            <Box className={styles.smallField}>
              <SimpleTextInput
                label="XP"
                name={`${fieldPrefix}_xpAmount`}
                type="number"
                value={value.xpAmount ?? ""}
                onChange={(e: any) =>
                  onChange({
                    xpAmount:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
            </Box>
          )}

          {value.awardType === "PACK" && (
            <>
              <Box className={styles.field}>
                <SearchableInput
                  label="Pack"
                  name={`${fieldPrefix}_pack`}
                  selected={selectedPack}
                  options={packOptions}
                  onInputChange={onPackSearch}
                  onOptionSelect={(e: any) =>
                    onChange({ pack: e.target.value?.id ?? null })
                  }
                  placeholder="Select Pack"
                />
              </Box>
              <Box className={styles.smallField}>
                <SimpleTextInput
                  label="Count"
                  name={`${fieldPrefix}_count`}
                  type="number"
                  value={value.count ?? ""}
                  onChange={(e: any) =>
                    onChange({
                      count:
                        e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                />
              </Box>
            </>
          )}

          {value.awardType === "BOOSTER" && (
            <Box className={styles.field}>
              <SearchableInput
                label="Booster"
                name={`${fieldPrefix}_booster`}
                selected={selectedBooster}
                options={boosterOptions}
                onInputChange={onBoosterSearch}
                onOptionSelect={(e: any) =>
                  onChange({ booster: e.target.value?.id ?? null })
                }
                placeholder="Select Booster"
              />
            </Box>
          )}

          {/* Coins are the only reward with a choice worth making: three artworks, picked from the
              amount and overridable. XP has one image, and a pack or booster brings its own, so
              offering a dropdown there would only invite a mismatched picture. It belongs on this
              row, beside the amount that drives it, rather than down with the upload control. */}
          {value.awardType === "COINS" && (
            <Box sx={{ width: 160 }}>
              <SimpleSelectInput
                label="Default Reward Image"
                name={`${fieldPrefix}_rewardImageType`}
                value={effectiveRewardImageType ?? ""}
                options={coinImageOptions}
                onChange={(e: any) =>
                  onChange({ rewardImageType: e.target.value || null })
                }
              />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 1 }}>
          <FilePicker
            label="Upload Custom Image"
            name={`${fieldPrefix}_image`}
            showPreview={false}
            imageType={SLOT_IMAGE_TYPE}
            enableDimensionControl={true}
            metaData={{
              note: `Recommended: ${DEFAULT_IMAGE_DIMENSIONS[SLOT_IMAGE_TYPE].width} x ${DEFAULT_IMAGE_DIMENSIONS[SLOT_IMAGE_TYPE].height} pixels. Images are resized and converted to WebP.`,
            }}
            onFileSelect={(file: File | null, dims?: any) => {
              if (!file) {
                clearImage();
                return;
              }

              releasePreview();

              onChange({
                __file: file,
                __dims: dims ?? null,
                __previewUrl: URL.createObjectURL(file),
              });
            }}
          />

          {/* Sits under the upload control, next to its dimension note: this describes what happens
              when no image is uploaded, so above the controls it read as a rule about them. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            {priorityCaption}
          </Typography>
        </Box>
      </Box>

      {/* Fixed 180x140 whatever the artwork's own dimensions are: the box never grows with a large
          image, and never lets one reward's row stand taller than its neighbours. */}
      <Box sx={{ width: 180, flexShrink: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          Image preview
        </Typography>
        <ImagePreviewBox
          url={resolved?.url}
          height={140}
          emptyText="No image"
          badge={
            resolved ? (
              <Chip
                size="small"
                color={resolved.badge === "New" ? "primary" : "default"}
                label={resolved.badge}
              />
            ) : undefined
          }
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          {resolved?.caption ?? emptyCaption}
        </Typography>
        {(value.__previewUrl || value.imagePath) && (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={clearImage}
            sx={{ mt: 0.5 }}
          >
            Remove image
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default RewardFields;
